import { StorageError } from './storageError';

export type StorageType = 'localStorage' | 'sessionStorage';

export interface StorageConfig {
  type: StorageType;
  prefix?: string;
}

export class StorageService {
  private storage: Storage;

  private prefix: string;

  private isSupported: boolean;

  constructor(config: StorageConfig = { type: 'localStorage' }) {
    this.prefix = config.prefix ? `${config.prefix}_` : 'salesforce_chat_';
    this.isSupported = StorageService.checkStorageSupport(config.type);

    if (this.isSupported) {
      this.storage =
        config.type === 'localStorage' ? localStorage : sessionStorage;
    } else {
      // Fallback to in-memory storage
      this.storage = StorageService.createMemoryStorage();
    }
  }

  private static checkStorageSupport(type: StorageType): boolean {
    try {
      const storage = type === 'localStorage' ? localStorage : sessionStorage;
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        // Check the actual storage type being tested, not always localStorage
        (() => {
          try {
            const storage =
              type === 'localStorage' ? localStorage : sessionStorage;
            return storage && storage.length !== 0;
          } catch {
            return false;
          }
        })()
      );
    }
  }

  isStorageSupported(): boolean {
    return this.isSupported;
  }

  setItem<T>(key: string, value: T): void {
    try {
      // Handle undefined explicitly since JSON.stringify(undefined) returns undefined
      const serialized =
        value === undefined ? 'undefined' : JSON.stringify(value);
      this.storage.setItem(this.prefix + key, serialized);
    } catch (error) {
      throw new StorageError(
        `Failed to set item "${key}": ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'setItem'
      );
    }
  }

  getItem<T>(key: string): T | null | undefined {
    try {
      const item = this.storage.getItem(this.prefix + key);
      if (item === null) {
        return null;
      }
      // Handle explicit undefined storage
      if (item === 'undefined') {
        return undefined;
      }
      const parsed = JSON.parse(item) as T;
      return parsed;
    } catch (error) {
      throw new StorageError(
        `Failed to get item "${key}": ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'getItem'
      );
    }
  }

  removeItem(key: string): void {
    try {
      this.storage.removeItem(this.prefix + key);
    } catch (error) {
      throw new StorageError(
        `Failed to remove item "${key}": ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'removeItem'
      );
    }
  }

  hasItem(key: string): boolean {
    try {
      return this.storage.getItem(this.prefix + key) !== null;
    } catch (error) {
      // If getItem throws, assume item is not accessible or doesn't exist
      return false;
    }
  }

  clear(): void {
    try {
      // Only clear items with the current prefix
      this.getAllKeys().forEach((key) => this.removeItem(key));
    } catch (error) {
      throw new StorageError(
        `Failed to clear storage: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'clear'
      );
    }
  }

  getAllKeys(): string[] {
    try {
      // Use the standard Storage interface approach for both browser and in-memory storage
      // Now that we've fixed the length property to be dynamic, this works for both
      const keys: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keys.push(key.replace(this.prefix, ''));
        }
      }
      return keys;
    } catch (error) {
      throw new StorageError(
        `Failed to get all keys: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'getAllKeys'
      );
    }
  }

  getSize(): number {
    try {
      // Use the standard Storage interface approach for both browser and in-memory storage
      // Now that we've fixed the length property to be dynamic, this works for both
      let total = 0;
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(this.prefix)) {
          const item = this.storage.getItem(key);
          if (item) {
            total += key.length + item.length;
          }
        }
      }
      return total;
    } catch (error) {
      throw new StorageError(
        `Failed to calculate storage size: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'getSize'
      );
    }
  }

  // Convenience methods for chat-specific storage
  setConversationId(id: string): void {
    this.setItem('conversationId', id);
  }

  getConversationId(): string | null {
    return this.getItem<string>('conversationId') || null;
  }

  setAuthToken(token: string): void {
    this.setItem('authToken', token);
  }

  getAuthToken(): string | null {
    return this.getItem<string>('authToken') || null;
  }

  setPrechatData(data: Record<string, unknown>): void {
    this.setItem('prechatData', data);
  }

  getPrechatData(): Record<string, unknown> | null {
    return this.getItem<Record<string, unknown>>('prechatData') || null;
  }

  clearSession(): void {
    this.removeItem('conversationId');
    this.removeItem('authToken');
    this.removeItem('prechatData');
  }

  private static createMemoryStorage(): Storage {
    const store: Record<string, string> = {};

    return {
      get length() {
        return Object.keys(store).length;
      },
      clear: () => {
        Object.keys(store).forEach((key) => delete store[key]);
      },
      getItem: (key: string) => store[key] || null,
      key: (index: number) => {
        const keys = Object.keys(store);
        return keys[index] || null;
      },
      removeItem: (key: string) => {
        delete store[key];
      },
      setItem: (key: string, value: string) => {
        store[key] = value;
      },
    };
  }
}
